'use client';

import { useRef, useState, useCallback, useMemo } from 'react';
import { Condition } from '@/utils/react';
import { useFiboService } from '@/hooks/fibo/useFiboService';

import { useOutsideClickEffect } from '@/hooks/utils';

import { FibosearchCombobox } from '@/components/fibosearch/FibosearchCombobox/FibosearchCombobox';
import { CollectionGroup } from '@/components/fibosearch/FibosearchCombobox/CollectionGroup';
import { CollectionList } from '@/components/fibosearch/FibosearchCombobox/CollectionList';
import HeaderInput from './HeaderInput';
import { useSafeRouter } from '@/hooks/route/useSafeRouter';
import type { FiboCollectionComboboxItem } from '@/models/fibo/fibosearch';
import { createFiboCollection } from '@/utils/domain/fibo';
import { Flex } from '@chakra-ui/react';
import { debounce } from 'es-toolkit';
import { env } from '@/utils/env';

const HeaderComboboxSearchInput = () => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const [comboboxContentRef, setComboboxContentRef] = useState<HTMLDivElement | null>(null);
  const router = useSafeRouter();

  const { useFiboSearch } = useFiboService();
  const { data: fiboSearchData } = useFiboSearch({ s: inputValue });

  const handleDebounceInputChange = useCallback((details: { inputValue: string }) => {
    const debouncedFn = debounce((value: string) => {
      setInputValue(value);
    }, 200);
    debouncedFn(details.inputValue);
    return () => {
      debouncedFn.cancel();
    };
  }, []);

  const handleInputClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(true);
  }, []);

  const handleInputFocus = useCallback(() => {
    setIsOpen(true);
  }, []);

  const suggestions = useMemo(() => fiboSearchData?.suggestions ?? [], [fiboSearchData]);

  const { collection, groupedCollection, isGroupedCollection, convertedCollectionComboboxItems } = useMemo(
    () => createFiboCollection(suggestions),
    [suggestions],
  );

  const shouldDisplayContent = inputValue.length > 0 && isOpen && suggestions.length > 0;

  const handleNavigate = useCallback(
    (url: string) => {
      router.push(url);
    },
    [router],
  );
  const handleSearch = () => {
    handleNavigate(`${env('NEXT_PUBLIC_ENV_FRONTEND_ORIGIN')}?s=${inputValue}&post_type=product`);
  };

  useOutsideClickEffect(comboboxContentRef, () => {
    if (document.activeElement !== inputRef.current) {
      setIsOpen(false);
    }
  });

  return (
    <FibosearchCombobox.Root
      collection={collection}
      loopFocus
      onInputValueChange={handleDebounceInputChange}
      closeOnSelect={false}
      open
      placeholder="Find your products"
      selectionBehavior="preserve"
      onValueChange={(value) => {
        const collection = value.items[0] as FiboCollectionComboboxItem;
        handleNavigate(collection?.url ?? '');
      }}
      positioning={{
        offset: {
          mainAxis: 4,
          crossAxis: 0,
        },
      }}
    >
      <FibosearchCombobox.Control display="flex" width="100%">
        <FibosearchCombobox.Input asChild>
          <HeaderInput
            ref={inputRef}
            onClick={handleInputClick}
            onFocus={handleInputFocus}
            onKeyDown={(e) => {
              console.info('Key pressed:', e.key);
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
        </FibosearchCombobox.Input>
      </FibosearchCombobox.Control>

      <FibosearchCombobox.Positioner>
        <FibosearchCombobox.Content>
          <Flex
            flexDirection="column"
            backgroundColor="white"
            paddingY="8px"
            paddingX="4px"
            ref={setComboboxContentRef}
            display={shouldDisplayContent ? 'block' : 'none'}
          >
            <Condition
              expression={isGroupedCollection}
              then={<CollectionGroup groupedCollection={groupedCollection} inputValue={inputValue} />}
              else={<CollectionList collections={convertedCollectionComboboxItems} inputValue={inputValue} />}
            />
          </Flex>
        </FibosearchCombobox.Content>
      </FibosearchCombobox.Positioner>
    </FibosearchCombobox.Root>
  );
};

export default HeaderComboboxSearchInput;
